import { Box, Button, List, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { Bound } from "lib/@components/binding/Bound.js"
import { Delayed } from "lib/@components/delayed.js"
import { Else } from "lib/@components/else.js"
import { If } from "lib/@components/if.js"
import { ListItemBox } from "lib/@components/ListItemBox.js"
import { Loader } from "lib/@components/Loader.js"
import { NoResults } from "lib/@components/no-results/no-results.js"
import noop from "lib/noop.js"
import { filterSchedules } from "~/ioc.js"
import { parentTreeItem } from "library/tree/index.mjs"
import { LIVE_TREE, SCHEDULE } from "library/constants.mjs"
import Iconify from "minimals-template/components/Iconify.js"
import { TailoredScheduleBrowserSearchBar } from "routes/schedule/plugins/custom-schedule-from-tailored/components/tailored-schedule-browser-search-bar.js"
import { ScheduleItemList } from "routes/schedule/components/schedule-item-list.js"
import { useTreeItem } from "routes/schedule/lib/useTreeItem.js"
import { mappingSort } from "routes/schedule/tree/schedules/mappingSort.js"
import { getTailoredScheduleNodeIds } from "routes/schedule/plugins/custom-schedule-from-tailored/controller/get-tailored-schedule-node-ids.controller.js"
import { normalizeNodeId } from "routes/schedule/plugins/custom-schedule-from-tailored/lib/normalize-node-id.js"
import { getFormattedNodeName } from "routes/schedule/plugins/custom-schedule-from-tailored/lib/get-formatted-node-name.js"

/* a cache-first query resolves in a tick, so only a fetch that actually goes to the network
 * outlives this grace period and shows the indicator
 */
const LOADING_INDICATOR_DELAY = 200

/*
 * A minimal schedule selector over one stage's tree
 * - the root lists regimes; once inside one, only paths leading to a tailored schedule are shown
 * - picking a schedule is passed up as {schedule, treeId} so the caller knows which regime it came from
 * - styling mirrors regime schedule item list search, minus unnecessary regime-context-dependant info
 */
export function TailoredScheduleBrowser({ rootId, selectedSchedule, onSelect = noop }) {
    const [path, setPath] = useState([rootId])

    useEffect(() => {
        // reset path if regime tree changes
        setPath([rootId])
    }, [rootId])

    const [search, setSearch] = useState("")
    const parentId = path.at(-1)
    const parent = useTreeItem(parentId, true)

    const treeId = path[1] ?? null // the regime is the first step below the stage root
    const { data: tailoredPaths, loading } = getTailoredScheduleNodeIds.useResults.status({
        regimeId: treeId,
        includeGroupNodes: true,
    })
    const loadingTailoredPaths = !!treeId && loading

    const items = (parent?.children ?? []).sortBy(mappingSort)
    const validItems = filterSchedules(items).filter(isOnTailoredPath).filter(matchesSearch)

    if (!parent) return <NoResults />

    return (
        <Bound
            parentId={parentId}
            parentItem={parent}
            shownItems={validItems}
            onClickSchedule={selectSchedule}
            transformScheduleItem={highlightSelected}
            inSelector={true} // hides selection checkboxes
        >
            <ListItemBox spacing={1} alignItems="center">
                <If truthy={path.length > 1}>
                    <Button
                        size="small"
                        onClick={navigateUp}
                        data-cy="tailored-schedule-list-up"
                        startIcon={<Iconify icon="mdi:arrow-upward" />}
                        sx={{ color: "text.secondary", whiteSpace: "nowrap", "&:hover": { color: "text.primary" } }}
                    >
                        {getParentLabel()}
                    </Button>
                </If>
                <Typography variant="subtitle2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {getNodeLabel()}
                </Typography>
            </ListItemBox>
            <TailoredScheduleBrowserSearchBar state={search} stateSetter={setSearch} />
            <List component="div" dense sx={{ px: 1 }}>
                <If truthy={loadingTailoredPaths}>
                    <Delayed time={LOADING_INDICATOR_DELAY}>
                        <Box>
                            <Loader inline color="primary" />
                        </Box>
                    </Delayed>
                    <Else>
                        <Box data-cy="tailored-schedule-list">
                            <ScheduleItemList items={validItems} select={select} />
                        </Box>
                    </Else>
                </If>
            </List>
        </Bound>
    )

    function getParentLabel() {
        if (path.length > 1) return getFormattedNodeName(parent[parentTreeItem]) || "Back"
        return ""
    }

    function getNodeLabel() {
        if (path.length === 1) return rootId === LIVE_TREE ? "Your live regimes" : "Your draft regimes"
        return getFormattedNodeName(parent)
    }

    // at the stage root there is no regime picked yet so every node is accepted as they are regimes with no need for filtering
    function isOnTailoredPath(item) {
        if (!treeId) return true
        if (loadingTailoredPaths) return false // ongoing request -> tailoredPaths still holds the previous regime's
        return !!tailoredPaths?.includes?.(normalizeNodeId(item))
    }

    function matchesSearch(item) {
        if (!search) return true
        const term = search.toLowerCase()

        return !!item.label?.toLowerCase().includes(term) || !!item.code?.toLowerCase().startsWith(term)
    }

    // ScheduleItemList sx wrapper
    function highlightSelected(children, item) {
        if (!selectedSchedule?.id || item.id !== selectedSchedule.id) return children

        return <Box sx={{ bgcolor: "action.selected" }}>{children}</Box>
    }

    function selectSchedule(schedule) {
        if (!treeId) return
        onSelect({ schedule, treeId })
    }

    function select(item) {
        return function () {
            if (item.type === SCHEDULE) return
            if (!item.hasChildren && !item.children) return

            setSearch("")
            setPath([...path, item.id])
        }
    }

    function navigateUp() {
        if (path.length === 1) return

        setSearch("")
        setPath((visited) => visited.slice(0, -1))
    }
}
